<template>
  <!-- fallback-on-body: this list lives inside the SigningWorkflowDialog (a
       Vuetify v-dialog whose content is position: absolute). On touch
       devices SortableJS always drags via its JS "fallback" ghost (iOS/
       Android Chrome never use native HTML5 DnD, see supportDraggable in
       sortablejs), and that ghost is normally sized/positioned relative to
       this component's own root element. That root has no CSS positioning
       context of its own, so the browser resolves the ghost's
       position: absolute/fixed against the dialog's positioned ancestor
       instead, badly offsetting the clone on tablet/mobile. fallback-on-body
       forces both the ghost's rect calculation and its DOM append target to
       document.body, which is never itself positioned, eliminating the
       mismatch. -->
  <Draggable
    v-if="createStore.document?.signeesOrdered"
    v-model="draggableGroups"
    animation="200"
    :fallback-on-body="true"
    ghost-class="group-ghost"
    group="group"
    handle=".group-grip-icon"
    item-key="index"
    @change="onGroupDragUpdate"
    @end="dragging = false"
    @start="dragging = true"
  >
    <template #item="{ element: groupItem, index: groupIndex }">
      <InviteesDraggableGroup
        :key="groupIndex"
        class="sortable"
        :class="{
          'multi-item-group': groupItem.length > 1,
          'empty-group': groupItem.length === 0,
          highlight:
            !hiddenGroupIndexes.includes(groupIndex) &&
            currentDraggedGroupIndex !== null &&
            firstOrLast(groupIndex),
          drag: dragging || hiddenGroupIndexes.includes(groupIndex),
          'current-drag':
            groupIndex === currentDraggedGroupIndex && groupItem.length === 1
        }"
        :disabled="disableDrag"
        :dragging="dragging"
        :show-drag-notch="groupItem.length > 1"
      >
        <template #content>
          <Draggable
            animation="200"
            :disabled="disableDrag"
            :fallback-on-body="true"
            ghost-class="item-ghost"
            group="items"
            handle=".grip-icon"
            :item-key="groupIndex.toString()"
            :list="groupItem"
            @change="onItemDragUpdate"
            @end="endItemDragging"
            @start="startItemDragging(groupIndex)"
          >
            <template #item="{ element, index }">
              <div class="d-flex flex-row align-center">
                <v-icon
                  class="grip-icon"
                  :class="{ dragging: draggingItem }"
                  color="grey-darken-1"
                  data-test-id="drag-item"
                  size="medium"
                >
                  fas fa-grip-dots-vertical
                </v-icon>
                <EmailSuggestion
                  :id="`email-${element.signeeId}`"
                  :key="`${element.signeeId}-${groupIndex}-${index}`"
                  v-model.trim="element.email"
                  :autocomplete="`${element.signeeId}`"
                  class="my-3"
                  :data-test-id="`signee-email-${element.email}`"
                  :data-test-sign-order="`sign-order-${Math.ceil(groupIndex / 2)}`"
                  :enable-groups="element.signeeType === SigneeType.SIGN"
                  :label="getFieldLabel(element.signeeType)"
                  :name="`${element.signeeId}-${groupIndex}-${index}`"
                  :rules="[ruleEmail]"
                  :validate-on="getValidationEvent(element.email)"
                  @blur="emit('on-blur', element)"
                  @focus="emit('on-focus', element)"
                  @group-members-selected="
                    (emails) => onGroupMembersSelected(emails, element)
                  "
                  @keydown.enter="
                    emit('on-update-key-down', {
                      event: $event,
                      signee: element
                    })
                  "
                  @update:model-value="
                    emit('on-update', { event: $event, signee: element })
                  "
                >
                  <template #prepend-inner>
                    <v-icon
                      v-if="createStore.hasApprovers"
                      color="primary"
                      data-test-id="type"
                      >{{
                        element?.signeeType === SigneeType.SIGN
                          ? 'fal fa-pen-nib'
                          : 'fal fa-octagon-check'
                      }}</v-icon
                    >
                  </template>
                </EmailSuggestion>
                <UpdateOrderInput
                  :group-index="Math.ceil(groupIndex / 2)"
                  :invitee="element"
                  @disable-drag="onDisableDrag"
                />
                <v-btn
                  class="text-grey mx-1"
                  color="default"
                  :data-test-id="`ordered-remove-signee-${element.email}`"
                  icon="fal fa-xmark"
                  size="medium"
                  @click="emit('remove-invitee', element)"
                >
                  <v-icon>fal fa-xmark</v-icon>
                </v-btn>
              </div>
            </template>
          </Draggable>
        </template>
        <!-- Quorum (requiredSignatures / requiredApprovals): an envelope-only
             feature, on groups with 2+ members, requiring either a real
             sequence (2+ groups) or, with a single group, 2+ members in it.
             Mixed groups show an explanatory message instead of the controls.
             See useQuorumEligibility for the rule (shared with the ungrouped
             view in UnorderedInvitees). -->
        <template v-if="quorumControl && canShowGroupQuorum(groupItem)" #footer>
          <GroupQuorumControl
            :approver-group="isApproverGroup(groupItem)"
            class="px-3 pb-2"
            :mixed-group="isMixedGroup(groupItem)"
            :quorum="createStore.getGroupQuorum(groupItem)"
            :total="groupItem.length"
            @change="(value) => createStore.setGroupQuorum(groupItem, value)"
          />
        </template>
      </InviteesDraggableGroup>
    </template>
  </Draggable>
</template>

<script setup lang="ts">
import UpdateOrderInput from '@/components/create/UpdateOrderInput.vue';
import InviteesDraggableGroup from '@/components/create/InviteesDraggableGroup.vue';
import GroupQuorumControl from '@/components/create/GroupQuorumControl.vue';
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import Draggable from 'vuedraggable/src/vuedraggable.js';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { useCreateStore } from '@/stores/create.ts';
import { computed, ref } from 'vue';
import type { Signee } from '@/types/Signee.ts';
import { useI18n } from 'vue-i18n';
import { ruleEmail } from '@deepcloud/field-validators';
import { persistSigneePlaceholders } from '@/composables/placement/usePlacementUtility';
import { buildFileAutographPositionsFromSignings } from '@/utils/signeeSignings.ts';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import { useGlobalStore } from '@/stores/global';
import { useDisplay } from 'vuetify';
import { useEmitter } from '@/composables/useEmitter';
import { useQuorumEligibility } from '@/components/create/composables/useQuorumEligibility.ts';
import { useQuorumControlSetting } from '@/composables/useQuorumControlSetting.ts';

const dragging = ref(false);
const disableDrag = ref(false);
const createStore = useCreateStore();
const globalStore = useGlobalStore();
const display = useDisplay();
const emitter = useEmitter();
const { t } = useI18n();
const { canShowGroupQuorum, isMixedGroup } = useQuorumEligibility();
const { quorumControl } = useQuorumControlSetting();

const { placePlaceholdersAutomatically, placeholderTemplateRefs } =
  injectStrict(PlaceHolderComposableKey);

const hiddenGroupIndexes = ref<Array<number>>([]);
const currentDraggedGroupIndex = ref<number | null>(null);
const draggingItem = ref(false);

const emit = defineEmits([
  'remove-invitee',
  'on-update-invitee',
  'on-blur',
  'on-focus',
  'on-update',
  'on-update-key-down'
]);

const draggableGroups = computed<Array<Array<Signee>>>(() => {
  const arrayWhitEmptySlots: Array<Array<Signee>> = [[]];
  createStore.signeesOrdered.forEach((group: Array<Signee>) => {
    arrayWhitEmptySlots.push([...group] as Array<Signee>);
    arrayWhitEmptySlots.push([]);
  });
  return arrayWhitEmptySlots;
});

// True when a (non-mixed) group consists of approvers — only the quorum
// wording differs between approving and signing.
function isApproverGroup(group: Array<Signee>): boolean {
  return group.every((signee) => signee.signeeType === SigneeType.APPROVE);
}

function getValidationEvent(email: string) {
  if (!email) {
    return 'submit';
  } else {
    return 'blur';
  }
}

function firstOrLast(index: number) {
  return index === 0 || index === (draggableGroups.value.length - 1 || 0);
}

// The first email updates the triggering field via updateInvitee API
// Remaining emails get created as new invitees.
// In multi-group mode, they form a new sign-order group inserted before the last one (the active input).
async function onGroupMembersSelected(
  emails: string[],
  signee: Signee
): Promise<void> {
  if (emails.length === 0) {
    emitter.$emit('close-group-members-dialog');
    return;
  }

  try {
    // Capture before adds: new signees clear per-file completeness and the
    // PlaceSignaturesSwitch watcher sets placeSignaturesModel false.
    //
    // Skipped while the signing-workflow dialog defers persistence: the invitees
    // added here only exist in the local draft, so auto-placing them would draw
    // their placeholders on the PDF behind the open dialog before Confirm.
    // useSigningWorkflow.confirm() places them once they really exist.
    const shouldAutoPlace =
      createStore.placeSignaturesModel && !createStore.deferPersistence;
    // Update the exact field that triggered the dialog with the first email
    const response = await createStore.updateInvitee(
      createStore.document!.documentId,
      signee.signeeId,
      {
        email: emails[0] === '' ? null : emails[0],
        ...(signee.policy?.canModifyAutographPosition && {
          autographPosition: signee.autographPosition,
          fileAutographPositions:
            buildFileAutographPositionsFromSignings(signee.signings) ?? {}
        }),
        comment: null
      }
    );
    createStore.updateInviteeInLocalData(response.data);

    // Create new invitees for the remaining emails
    const remainingEmails = emails.slice(1);

    if (remainingEmails.length > 0) {
      const results = await Promise.all(
        remainingEmails.map((email) =>
          createStore.addInvitee(createStore.document!.documentId, {
            email,
            signeeType: SigneeType.SIGN
          })
        )
      );

      const signeesOrdered = createStore.signeesOrdered;
      const isMultiGroup = signeesOrdered.length > 1;

      if (isMultiGroup) {
        // Find the group that contains the triggering signee
        // and add the new invitees to that same group
        const targetGroup = signeesOrdered.find((group) =>
          group.some((_signee) => _signee.signeeId === signee.signeeId)
        );

        if (targetGroup) {
          results.forEach((res) => {
            targetGroup.push(res.data);
          });
        }

        createStore.updateSignOrderInLocalData(signeesOrdered);
      } else {
        // Single group mode: push new invitees into signeesOrdered[0]
        results.forEach((res) => {
          signeesOrdered[0].push(res.data);
        });
      }
    }

    // If auto-placement is enabled, reposition signature placeholders
    if (shouldAutoPlace) {
      await placeSignaturesAutomatically();
    }
  } catch (e) {
    console.error('Failed to add group members:', e);
  } finally {
    emitter.$emit('close-group-members-dialog');
  }
}

async function placeSignaturesAutomatically(): Promise<void> {
  setTimeout(async () => {
    if (createStore.filteredSignees) {
      const placeholdersToUpdate = await placePlaceholdersAutomatically(null);

      await persistSigneePlaceholders(
        createStore,
        placeholdersToUpdate,
        placeholderTemplateRefs?.value,
        createStore.filteredSignees,
        createStore.document
      );

      if (display.mdAndDown.value) {
        globalStore.drawer = false;
      }
    }
  }, 600);
}

// ─── Drag and drop handlers ─────────────────────────────────────────

function onGroupDragUpdate(event) {
  const { moved } = event;

  const oldIndex = moved.oldIndex / 2 - 0.5;
  const difference = (moved.newIndex - moved.oldIndex) / 2;
  const newIndex = oldIndex + difference;

  const tempData = createStore.signeesOrdered;
  const movedGroup = tempData.splice(oldIndex, 1);

  tempData.splice(newIndex, 0, ...movedGroup);

  const updatedGroups = tempData.map((group, index) =>
    group.map((s) => ({ ...s, signOrder: index }))
  );

  createStore.signeesOrdered = updatedGroups;
}

function startItemDragging(index: number) {
  draggingItem.value = true;
  currentDraggedGroupIndex.value = index;
  if (draggableGroups.value[index].length === 1) {
    hiddenGroupIndexes.value = [index - 1, index + 1];
  }
}

function endItemDragging() {
  draggingItem.value = false;
  currentDraggedGroupIndex.value = null;
  hiddenGroupIndexes.value = [];
}

function onItemDragUpdate() {
  const newOrderedGroups = draggableGroups.value.filter(
    (group) => group.length > 0
  );
  createStore.updateSignOrderInLocalData(newOrderedGroups);
}

function onDisableDrag(disableDragValue: boolean) {
  disableDrag.value = disableDragValue;
}

function getFieldLabel(signeeType: SigneeType) {
  return signeeType === SigneeType.SIGN
    ? t('labels.signee_email')
    : t('labels.approver_email');
}
</script>

<style scoped lang="scss">
:deep(.v-field__input) {
  padding-right: 44px;
}

.grip-icon {
  padding: 6px 4px;
  margin: 2px;
  border-radius: 4px;
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;

  &:hover {
    background-color: rgb(var(--v-theme-primary));
    color: white !important;
    cursor: grab;
  }

  &.active {
    cursor: -webkit-grabbing;
    cursor: -moz-grabbing;
    cursor: grabbing;
  }

  &.dragging {
    background-color: unset !important;
    color: unset !important;
  }
}

.multi-item-group {
  padding-top: 6px;
}

.empty-group {
  display: block;
  border: none !important;
  border-radius: 0 !important;
  background-color: transparent !important;
  margin: 2px 0;
  padding: 4px;

  &.highlight {
    border-radius: 4px !important;
    background-color: rgb(var(--v-theme-grey-lighten-5)) !important;
    border: solid 1px rgb(var(--v-theme-primary)) !important;
    margin: 8px 0;
    min-height: 8px !important;
  }

  &.drag {
    display: none;
  }
}

.current-drag {
  background-color: transparent !important;
  border: 1px dashed rgb(var(--v-theme-grey-lighten-5)) !important;
  margin-top: 6px !important;
  margin-bottom: 8px !important;
  min-height: 8px !important;
}

.group-ghost {
  border: dashed 1px !important;
  opacity: 0.5;
  margin-bottom: 8px;
  margin-top: 8px;

  :deep(.signee-group-selector) {
    border: 1px dashed;
    border-right: 1px solid rgb(var(--v-theme-grey-lighten-5));
  }
}

:deep(.item-ghost) {
  background-color: rgb(var(--v-theme-grey-lighten-5));
  border: solid 1px rgb(var(--v-theme-primary)) !important;
  border-radius: 5px;
  padding-bottom: 4px;
  margin-top: 4px;
  opacity: 0.5;
}
</style>
